import { useEffect, useMemo, useRef, useState } from "react";
import { MAX_DELTA_YEARS } from "../../classes/constants/validationConstants";
import {
  formatDateForInput,
  formatDateToTimestamp,
  isDateWithinMax,
  isDateWithinMin,
  normalizeDateInput,
} from "../../classes/utils/ValidationUtils";
import "../../errorAnimations.css";
import useTranslation from "../../hooks/i18n/useTranslation";
import Tooltip, { TooltipOrientation } from "../tooltip/Tooltip";
import "./DatePicker.css";

const DatePickerComponent = ({
  name = "",
  timestamp = undefined,
  handleChange = () => {},
  placeholderText = "Date",
  locale = "",
  maxDate = undefined,
  minDate = undefined,
  error = null,
  onValidationError = null,
  validationPulseKey = 0,
  tooltipText,
  tooltipContent,
  tooltipOrientation = TooltipOrientation.Top,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [animateError, setAnimateError] = useState(false);
  const pulseRef = useRef(validationPulseKey);
  const inputRef = useRef(null);
  const handleChangeRef = useRef(handleChange);
  const maxDateValue = useMemo(() => normalizeDateInput(maxDate), [maxDate]);
  const minDateValue = useMemo(() => normalizeDateInput(minDate), [minDate]);
  const defaultTooltipText = t("455"); // "Show calendar"

  useEffect(() => {
    handleChangeRef.current = handleChange;
  }, [handleChange]);

  useEffect(() => {
    if (error) {
      setAnimateError(false);
      requestAnimationFrame(() => setAnimateError(true));
      return;
    }
    if (validationPulseKey !== pulseRef.current) {
      pulseRef.current = validationPulseKey;
      setAnimateError(false);
      requestAnimationFrame(() => setAnimateError(true));
      return;
    }
    setAnimateError(false);
  }, [error, validationPulseKey]);

  const handleAnimationEnd = () => {
    setAnimateError(false);
  };

  // Convert timestamp to date string only when provided with a valid timestamp
  useEffect(() => {
    const date = normalizeDateInput(timestamp);
    if (!date) {
      setValue("");
      return;
    }
    let finalDate = date;
    let changed = false;

    if (maxDateValue && !isDateWithinMax(date, maxDateValue)) {
      finalDate = maxDateValue;
      changed = true;
    } else if (minDateValue && !isDateWithinMin(date, minDateValue)) {
      finalDate = minDateValue;
      changed = true;
    }

    if (changed) {
      handleChangeRef.current?.(formatDateToTimestamp(finalDate));
    }

    const formattedValue = formatDateForInput(finalDate);
    setValue(formattedValue);
  }, [timestamp, maxDateValue, minDateValue]);

  // Fixed change handler with proper validation
  function handleInputChange(e) {
    const inputValue = e.target.value;
    setValue(inputValue);

    // Handle empty input
    if (!inputValue) {
      onValidationError?.(name, null);
      handleChangeRef.current?.(null);
      return;
    }

    // Only propagate if we have a complete, valid date in YYYY-MM-DD format
    if (inputValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parsedDate = normalizeDateInput(inputValue);

      if (!parsedDate) {
        // Invalid date (e.g., 2024-13-45), don't propagate
        onValidationError?.(name, "Please enter a valid date");
        return;
      }

      const year = parsedDate.getFullYear();

      // Check year range validity
      if (year < 1900 || year > 2100) {
        // Out of reasonable range, don't propagate
        onValidationError?.(name, "Date is out of the allowed range");
        return;
      }

      // Valid date within range - propagate
      onValidationError?.(name, null);
      handleChangeRef.current?.(formatDateToTimestamp(parsedDate));
    }
  }

  function handleBlur() {
    setFocused(false);

    if (!value) {
      return;
    }

    if (!value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setValue("");
      onValidationError?.(name, "Please enter a valid date");
      handleChangeRef.current?.(null);
      return;
    }

    const parsedDate = normalizeDateInput(value);

    if (!parsedDate) {
      // Invalid date - clear it
      setValue("");
      onValidationError?.(name, "Please enter a valid date");
      handleChangeRef.current?.(null);

      return;
    }

    const clampToMaxDate = () => {
      if (maxDateValue) {
        const clampedValue = formatDateForInput(maxDateValue);
        setValue(clampedValue);
        handleChangeRef.current?.(formatDateToTimestamp(maxDateValue));
      } else {
        setValue("");
        handleChangeRef.current?.(null);
      }
    };

    const buildRangeErrorMessage = () => {
      if (minDateValue && maxDateValue) {
        return `Date must be between ${formatDateForInput(
          minDateValue
        )} and ${formatDateForInput(maxDateValue)}`;
      }
      if (maxDateValue) {
        return `Date must be on or before ${formatDateForInput(maxDateValue)}`;
      }
      if (minDateValue) {
        return `Date must be on or after ${formatDateForInput(minDateValue)}`;
      }
      return "Date is out of the allowed range";
    };

    const year = parsedDate.getFullYear();
    const maxYearLimit =
      (maxDateValue && maxDateValue.getFullYear()) ||
      new Date().getFullYear() + MAX_DELTA_YEARS;
    const minYearLimit = (minDateValue && minDateValue.getFullYear()) || 1900;

    if (year < minYearLimit || year > maxYearLimit) {
      clampToMaxDate();
      onValidationError?.(name, "Date is out of the allowed range");

      return;
    }

    // Clamp to maxDate whenever we are outside the allowed range
    if (maxDateValue && !isDateWithinMax(parsedDate, maxDateValue)) {
      clampToMaxDate();
      onValidationError?.(name, "Date is out of the allowed range");

      return;
    }

    if (minDateValue && !isDateWithinMin(parsedDate, minDateValue)) {
      clampToMaxDate();
      onValidationError?.(name, "Date is out of the allowed range");
      return;
    }

    onValidationError?.(name, null);
  }

  return (
    <>
      <div className="simple-date-picker">
        <input
          ref={inputRef}
          type="date"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          className={`date-input ${
            !value && !focused ? "empty" : ""
          } ${error ? "error-input" : ""}`}
          max={maxDateValue ? formatDateForInput(maxDateValue) : undefined}
          min={minDateValue ? formatDateForInput(minDateValue) : undefined}
          aria-invalid={!!error}
          onAnimationEnd={handleAnimationEnd}
          style={{
            animation: animateError
              ? "errorInputPulse 0.35s ease-in-out 0s 2"
              : "none",
          }}
        />
        {!value && !focused && (
          <div
            className="date-placeholder"
            onClick={() => inputRef.current?.focus()}
          >
            {placeholderText || "mm/dd/yyyy"}
          </div>
        )}
        <div className="date-picker-icon-tooltip-wrapper">
          <Tooltip
            text={tooltipText || defaultTooltipText}
            content={tooltipContent}
            orientation={tooltipOrientation}
          >
            <div
              className="date-picker-icon-tooltip-trigger"
              onClick={() => inputRef.current?.showPicker?.()}
            />
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default DatePickerComponent;
